import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import apiService from '../services/api';
import { RENAISSANCE_COLORS } from '../utils';

interface JobListItem {
  job_id: string;
  status: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  progress_percentage: number;
  rows_processed: number;
  total_rows?: number;
  config: {
    batch_size: number;
    max_samples?: number;
    dataset_s3_path?: string;
  };
}

function EvaluationsDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [autoRefresh] = useState(true);

  const loadJobs = useCallback(async () => {
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await apiService.listEvaluations(status, 50, 0);
      setJobs(response.jobs);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load jobs';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Auto-refresh every 5 seconds if there are running jobs and auto-refresh is enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const hasRunningJobs = jobs.some(job => job.status === 'running' || job.status === 'pending');
    if (!hasRunningJobs) return;

    const intervalId = setInterval(() => {
      loadJobs();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [jobs, autoRefresh, loadJobs]);

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleRowClick = (jobId: string) => {
    navigate(`/evaluations/${jobId}`);
  };

  const handleRefresh = () => {
    setLoading(true);
    loadJobs();
  };

  return (
    <div style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flex: 1 }}>
        <Paper sx={{ p: 3 }}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Evaluation Jobs
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="running">Running</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Jobs Table */}
          {loading && jobs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Alert severity="info">
              No evaluation jobs found. Start a new evaluation from the Run Evaluation page.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Batch Size</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow
                      key={job.job_id}
                      hover
                      onClick={() => handleRowClick(job.job_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {job.job_id.substring(0, 24)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.status.toUpperCase()}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(job.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {(job.status === 'running' || job.status === 'pending') ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={job.progress_percentage}
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption">
                              {job.progress_percentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption">
                            {job.status === 'completed' ? '100%' : 'N/A'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.rows_processed}
                          {job.total_rows ? ` / ${job.total_rows}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.config.batch_size}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(job.job_id);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Auto-refresh indicator */}
          {autoRefresh && jobs.some(job => job.status === 'running' || job.status === 'pending') && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Auto-refreshing every 5 seconds...
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      <footer style={{
        backgroundColor: RENAISSANCE_COLORS.white,
        padding: '12px 0',
        borderTop: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px'
        }}>
          <div style={{ fontSize: '12px' }}>
            Autoscoringevalsvc v0.1
          </div>
          <img
            src="/ren-cropped.svg"
            alt="Renaissance Logo"
            style={{ height: '30px' }}
          />
        </div>
      </footer>
    </div>
  );
}

export default EvaluationsDashboard;
